import { Id } from "@iyk/prelude"
import { Footer } from "@iyk/vf-ui"
import { Effect } from "effect"
import { Res } from "../../../../../../server/src/response.ts"
import { appConfig } from "../../../app-config.ts"
import type { Product } from "../../../core/products/product.ts"
import { useLang } from "../../../react-router/use-lang.ts"
import { Handler } from "../../../server/handler.ts"
import { BackToProfileButton } from "../../profile/ui/back-to-profile-button.tsx"
import { Shop } from "../shop.ts"
import { AddToCartSelector } from "../ui/add-to-cart-selector.tsx"
import { Header } from "../ui/header.tsx"
import type { Route } from "./+types/product-detail.ts"

export const meta: Route.MetaFunction = ({ data }) => [
  {
    title: `${data.product.title} | ${appConfig.artist.name}`
  },
  {
    property: "og:title",
    content: `${data.product.title} | ${appConfig.artist.name}`
  }
]

export const loader = Handler.make(
  "shop",
  ({ params, products, cookies, carts, cookieJar, api, sessions }) => {
    return Effect.gen(function* () {
      if (params.productId === undefined) {
        return yield* Effect.dieMessage("params.productId is undefined. this should never happen")
      }

      const product = products.findById(Id.make<Product>(params.productId))
      const productList = products.list
      const cart = Shop.getCart({ cookies, carts, cookieJar })
      const user = sessions.authenticated.pipe(
        Effect.flatMap((session) => api.profile(session.user)),
        Effect.orElseSucceed(() => undefined)
      )

      return yield* Effect.all({ product, productList, cart, user }, { concurrency: "unbounded" }).pipe(
        Effect.map(({ product, productList, cart, user }) =>
          Res.json({
            product: Shop.toProduct(product),
            products: productList.map(Shop.toProduct),
            cart,
            user
          })
        )
      )
    }).pipe(Effect.catchTags({ ProductNotFound: () => Effect.succeed(Res.notFound) }))
  }
)

export default function ProductDetailRoute({ loaderData }: Route.ComponentProps) {
  const lang = useLang()
  return (
    <div className="max-w-[var(--app-max-width)] w-full mx-auto">
      <div className="relative min-h-dvh flex flex-col text-foreground">
        <div className="absolute inset-0 w-full overflow-hidden select-none h-[calc(100vh*2/3)]">
          <img
            src={appConfig.assets.images.background}
            alt="Profile Background"
            className="size-full object-cover blur-md scale-110"
          />
          <div className="absolute inset-0 size-full bottom-0 left-0 bg-[linear-gradient(to_top,_var(--gray-1),_rgba(0,0,0,0.7))]" />
        </div>
        <Header user={loaderData.user} cart={loaderData.cart} />
        <main className="flex-1 size-full flex flex-col z-10 mt-2 px-4">
          <BackToProfileButton />
          <AddToCartSelector selectedProduct={loaderData.product} products={loaderData.products} />
        </main>
        <Footer social={appConfig.socialLinks} lang={lang} />
      </div>
    </div>
  )
}
